import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export const PRE_ASYNC_COLLECTION_FLAG = "pre-async-collection";

export type MedicationHistoryStep = "consent" | "fetching" | "picker";

export type ConsentKind = "surescripts_consent";

const MAX_HANDLED_IDS = 50;

type ActiveMedicationHistory = {
  conversationId: string;
  consentType: ConsentKind;
  step: MedicationHistoryStep;
};

type MedicationHistoryStore = {
  active: ActiveMedicationHistory | null;
  /**
   * Every card event already acted on, kept apart from `active` so it outlives dismissal. Each
   * event mounts its own gate, so remounting an older one must not restart the card.
   */
  handledMessageIds: string[];
  start: (
    conversationId: string,
    consentType: ActiveMedicationHistory["consentType"],
    messageId: string,
  ) => void;
  goTo: (step: MedicationHistoryStep) => void;
  clear: () => void;
};

/**
 * The medication-history card's state, kept outside the questions panel so it outlives messages.
 * Persisted so a reload mid-pull does not reopen the consent step. Scoped to the conversation, as
 * a pull is not attached to a clinic request.
 */
export const useMedicationHistoryStore = create<MedicationHistoryStore>()(
  persist(
    (set) => ({
      active: null,
      handledMessageIds: [],
      start: (conversationId, consentType, messageId) =>
        set((state) =>
          state.handledMessageIds.includes(messageId)
            ? state
            : {
                handledMessageIds: [
                  ...state.handledMessageIds.slice(-(MAX_HANDLED_IDS - 1)),
                  messageId,
                ],
                active: { conversationId, consentType, step: "consent" },
              },
        ),
      goTo: (step) =>
        set((state) =>
          state.active ? { active: { ...state.active, step } } : state,
        ),
      clear: () => set({ active: null }),
    }),
    {
      name: "medication-history",
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
);

/** Whether the persisted store has loaded, so the card does not flash the wrong step. */
export function useHasMedicationHistoryStoreHydrated(): boolean {
  const [hydrated, setHydrated] = useState(() =>
    useMedicationHistoryStore.persist.hasHydrated(),
  );
  useEffect(() => {
    const unsub = useMedicationHistoryStore.persist.onFinishHydration(() =>
      setHydrated(true),
    );
    // Hydration may have finished between the initial read and this subscription.
    if (useMedicationHistoryStore.persist.hasHydrated()) {
      setHydrated(true);
    }
    return unsub;
  }, []);
  return hydrated;
}
